import type { LabelNoId } from '@/data/Label';
import type { StaffId } from './Staff';

// check sort
// Actually parse items
// Generate type style (opacity, text)

/**
 * Describes a Label type, used to initialize the default FilterGroup
 * Slightly different from FilterArg for smaller DefaultTypes size
 */
export interface TypeArg {
  /**
   * The type and the tag, as displayed in the interface and present in .dez files
   */
  readonly type: string;
  readonly tag?: string;
  /**
   * If the types should be shown as an option to the user
   */
  readonly isLegacy?: boolean;
  /**
   * If no line is present in the dez file, use this one (should not be saved after)
   */
  readonly line?: StaffId;
  /**
   * The color of the label and associated UI elements
   * An 'auto' color means the color will be assigned automatically,
   * ideally between the colorHueStart and colorHueEnd hues.
   */
  readonly color?: string;
  readonly colorHueStart?: number;
  readonly colorHueEnd?: number;
  /**
   * The weight of the label text
   */
  readonly fontWeight?: string;
  /**
   * The style of the label text
   */
  readonly fontStyle?: string;
  /**
   * The size of the label text
   */
  readonly fontSize?: string;

  readonly styleType?: string;

  readonly symbol?: string;

  readonly icon?: string;
}

/**
 * Describes a filter to pass to a FilterGroup
 *
 * More information is then added by the FilterGroup
 */
export interface FilterArg {
  readonly tag: string | null;

  readonly type: string | null;

  readonly layers: string[];

  readonly line: StaffId | null;

  readonly color: string | null;
  readonly colorHueStart?: number;
  readonly colorHueEnd?: number;

  readonly fontWeight: string | null;

  readonly fontStyle: string | null;

  readonly fontSize: string | null;

  readonly styleType: string | null;

  readonly symbol: string | null;

  readonly icon: string | null;
}

/**
 * Describes a filter, or set of display rules
 *
 * Affects the css properties and default position of labels that match the type and layers
 */
export interface Filter extends FilterArg {
  /**
   * If the type and layers should be considered valid new values from the user
   */
  readonly displayable: boolean;
}

/**
 * Describes a group of filters
 *
 * Can match labels to rules
 *
 * Can generate css rules
 */
export default class FilterGroup {
  readonly filters: Filter[];

  constructor(filters: Filter[]) {
    this.filters = [...filters];
    // Sort filters by the number of rules defined, in descending order
    this.filters.sort((t1, t2) => (
      ((t1.type ? 1 : 0) + t1.layers.length)
      - ((t2.type ? 1 : 0) + t2.layers.length)));
  }

  /**
   * @returns a set of all unique types in the filters
   */
  getTypeNames(): Set<string> {
    return new Set(this.filters
      .filter((f) => f.displayable && f.type)
      .map((f) => f.type as string));
  }

  /**
   * @returns a set of all unique types in the layers
   */
  getLayersNames(): Set<string> {
    return new Set(this.filters
      .filter((f) => f.displayable && f.type)
      .map((f) => f.type as string));
  }

  /**
   * @param type the type of a label
   * @param layers the layers of a label
   * @returns the default line of the label if defined in the filters
   */
  getDefaultLine(type: string, tag: string, layers: string[]): StaffId | null {
    const layerSet = new Set(layers);

    // Finds
    const filter = this.filters.find((f) => f.line !== null
      && (!f.type || f.type === type)
      && (!f.tag || f.tag === tag)
      && f.layers.every((l) => layerSet.has(l)));

    return filter?.line ?? null;
  }

  /**
   * @param label a label
   * @returns the default style of the label, when defined in the filters
   */
  getStyle(label: LabelNoId): FilterArg | undefined {
    const layerSet = new Set(label.layers);

    // Finds
    const filter = this.filters.find((f) => (!f.type || f.type === label.type)
      && (!f.tag || f.tag === label.tag)
      && f.layers.every((l) => layerSet.has(l)));

    return filter;
  }

  styleType(label: LabelNoId): string {
    return this.getStyle(label)?.styleType || '';
  }

  symbol(label: LabelNoId): string | undefined {
    return this.getStyle(label)?.symbol || undefined;
  }

  icon(label: LabelNoId): string | undefined {
    const icon = this.getStyle(label)?.icon;

    return icon?.replace('{tag}', label.tag || '') || undefined;
  }

  /**
   * Generate a filter group from a list of types
   */
  static fromTypes(types: TypeArg[]): FilterGroup {
    const filters: Filter[] = types.map((filter) => ({
      layers: [],
      type: filter.type,
      tag: filter.tag || null,
      displayable: !filter.isLegacy,
      color: filter.color || null,
      colorHueStart: filter.colorHueStart || undefined,
      colorHueEnd: filter.colorHueEnd || undefined,
      fontWeight: filter.fontWeight || null,
      fontSize: filter.fontSize || null,
      fontStyle: filter.fontStyle || null,
      line: filter.line === undefined ? null : filter.line,
      styleType: filter.styleType || null,
      symbol: filter.symbol || null,
      icon: filter.icon || null,
    }));
    return new FilterGroup(filters);
  }

  /**
   * Generate a filter group from a list of user defined filters
   * TODO: Remove duplicated code if possible
   */
  static fromFilters(filterArgs: FilterArg[]): FilterGroup {
    const filters: Filter[] = filterArgs.map((filter) => ({
      layers: [...filter.layers || []].sort(),
      type: filter.type || null,
      tag: filter.tag || null,
      displayable: true,
      color: filter.color || null,
      colorHueStart: filter.colorHueStart || undefined,
      colorHueEnd: filter.colorHueStart || undefined,
      fontWeight: filter.fontWeight,
      fontSize: filter.fontSize,
      fontStyle: filter.fontStyle,
      line: filter.line === undefined ? null : filter.line,
      styleType: filter.styleType,
      symbol: filter.symbol,
      icon: filter.icon,
    }));
    return new FilterGroup(filters);
  }
}
